import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { DialogueId } from '@/core/content/ids';
import { entryNode, nextNodeId, visibleNode, type DialogueNode } from '@/core/domain/dialogue';
import { useUiImage } from '@/ui/art/uiArt';
import { useI18n } from '@/ui/i18n/I18nProvider';
import { useSave, useServices } from '@/ui/services/ServicesContext';
import styles from './DialogueOverlay.module.css';
import { useBack } from '@/ui/back/backStack';

const CHAR_MS = 30;

type Speaker = DialogueNode['speaker'];

/** Name-tag colours per speaker (ux-screens §3.12 "thẻ tên màu riêng NPC"). */
const TAG: Partial<Record<Speaker, string>> = {
  grandma: 'var(--c-blush)',
  grandma_letter: 'var(--c-blush)',
  grandma_note: 'var(--c-blush)',
  npc_lan: 'var(--c-mint)',
  npc_bao: 'var(--c-sky)',
  npc_mit: 'var(--c-butter)',
  npc_boss_cat: 'var(--c-lavender)',
  player: 'var(--c-cream-deep)',
  customer: 'var(--c-cream-deep)',
};

/** Portraits for speakers that are not NPCs (asset-list `por_grandma_*`); NPCs name theirs in content. */
const PORTRAIT: Partial<Record<Speaker, string>> = {
  grandma: 'por_grandma_neutral',
  grandma_letter: 'por_grandma_neutral',
  grandma_note: 'por_grandma_neutral',
};

/** `por_npc_lan_neutral` + `happy` → `por_npc_lan_happy` (style-guide §11.3). */
function withExpression(portrait: string | undefined, expression: string | undefined): string | undefined {
  if (!portrait || !expression || expression === 'neutral') return undefined;
  return `${portrait.replace(/_neutral$/, '')}_${expression}`;
}

function speakerKey(speaker: Speaker): string | null {
  if (speaker === 'narrator') return null;
  if (speaker.startsWith('npc_')) return `npc.${speaker}.name`;
  return `ui.speaker.${speaker}`;
}

/**
 * S12 dialogue (ux-screens §3.12): bottom overlay with portrait, name tag, typewriter text and ≤ 2 choices.
 * Tap anywhere (or Space/Enter) shows the whole line, then continues. Esc / [Bỏ qua] closes it.
 * Rewards never depend on reading to the end; leaving a node applies its effects (content-schemas §5.2).
 */
export function DialogueOverlay({
  dialogueId,
  onDone: onDoneProp,
}: {
  dialogueId: DialogueId;
  onDone: (skipped: boolean) => void;
}) {
  // Close exactly once, whichever way the dialogue ends.
  const doneRef = useRef(false);
  const onDone = useCallback(
    (skipped: boolean) => {
      if (doneRef.current) return;
      doneRef.current = true;
      onDoneProp(skipped);
    },
    [onDoneProp],
  );
  const { t } = useI18n();
  const { content, store, dispatch } = useServices();
  const motionPref = useSave((s) => s.settings.reducedMotion);
  const systemReduced = useMemo(() => window.matchMedia('(prefers-reduced-motion: reduce)').matches, []);
  const [node, setNode] = useState<DialogueNode | null>(() =>
    entryNode(content, store.getState(), dialogueId),
  );
  // Typewriter progress, tied to the node it belongs to so a new line starts from zero.
  const [typed, setTyped] = useState<{ nodeId: string | null; n: number }>({ nodeId: null, n: 0 });
  const nodeId = node?.id ?? null;
  const speaker = node?.speaker;
  const basePortrait = speaker ? (PORTRAIT[speaker] ?? content.npcs.get(speaker)?.portrait) : undefined;
  // The line's expression when that image exists, else the neutral portrait.
  const expressive = useUiImage(withExpression(basePortrait, node?.expression));
  const neutral = useUiImage(basePortrait);
  const portrait = expressive ?? neutral;
  const shown = typed.nodeId === nodeId ? typed.n : 0;

  const text = node ? t(node.textKey) : '';
  const animate = motionPref === 'off' || (motionPref === 'system' && !systemReduced);
  const full = !animate || shown >= text.length;

  useEffect(() => {
    if (!node) onDone(false);
  }, [node, onDone]);

  useEffect(() => {
    if (!animate) return;
    const timer = setInterval(() => {
      setTyped((p) => {
        const n = p.nodeId === nodeId ? p.n : 0;
        if (n >= text.length) clearInterval(timer);
        return { nodeId, n: n + 1 };
      });
    }, CHAR_MS);
    return () => {
      clearInterval(timer);
    };
  }, [nodeId, text, animate]);

  const advance = useCallback(
    (choiceIndex?: number) => {
      if (!node) return;
      if (!full && choiceIndex === undefined) {
        setTyped({ nodeId: node.id, n: text.length });
        return;
      }
      if (node.choices && choiceIndex === undefined) return; // wait for a choice
      dispatch({
        type: 'advanceDialogue',
        dialogueId,
        nodeId: node.id,
        ...(choiceIndex !== undefined ? { choiceIndex } : {}),
      });
      const next = visibleNode(content, store.getState(), dialogueId, nextNodeId(node, choiceIndex));
      setNode(next);
    },
    [node, full, text.length, dispatch, dialogueId, content, store],
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // A focused button already turns Space/Enter into a click.
      const onButton = (e.target as HTMLElement | null)?.tagName === 'BUTTON';
      if ((e.code === 'Space' || e.code === 'Enter') && !onButton) {
        e.preventDefault();
        advance();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [advance, onDone]);

  useBack(() => {
    onDone(true);
  });

  if (!node) return null;
  const nameKey = speakerKey(node.speaker);
  const isLetter = node.speaker === 'grandma_letter' || node.speaker === 'grandma_note';
  const name = nameKey ? t(nameKey) : null;

  return (
    <div
      className={styles.overlay}
      role="dialog"
      aria-modal="true"
      aria-label={name ?? t('ui.dialogue.label')}
      data-testid="dialogue"
    >
      {/* Tap anywhere to continue: a full-size button under the (click-through) dialogue box. */}
      <button
        type="button"
        className={styles.backdrop}
        aria-label={t('ui.dialogue.next')}
        data-testid="dialogue-next"
        onClick={() => {
          advance();
        }}
      />
      <div className={styles.stage}>
        {name && !isLetter && (
          <div className={styles.portrait} style={{ background: TAG[node.speaker] }} aria-hidden="true">
            {portrait ? (
              <img className={styles.portraitArt} src={portrait} alt="" draggable={false} />
            ) : (
              name.slice(name.lastIndexOf(' ') + 1, name.lastIndexOf(' ') + 2)
            )}
          </div>
        )}
        <div className={styles.box} data-letter={isLetter || undefined} data-narrator={!name || undefined}>
          {isLetter && portrait && (
            // Granny's small photo clipped to her letter, smiling (or not) with the line.
            <img className={styles.letterPhoto} src={portrait} alt="" aria-hidden="true" draggable={false} />
          )}
          {name && (
            <span className={styles.tag} style={{ background: TAG[node.speaker] }}>
              {name}
            </span>
          )}
          <p className={styles.text} aria-live="polite">
            {full ? text : text.slice(0, shown)}
            {full && !node.choices && (
              <span className={styles.more} aria-hidden="true">
                ▼
              </span>
            )}
          </p>
        </div>
        {full && node.choices && (
          <div className={styles.choices}>
            {node.choices.map((c, i) => (
              <button
                key={c.textKey}
                type="button"
                className={styles.choice}
                onClick={() => {
                  advance(i);
                }}
              >
                {t(c.textKey)}
              </button>
            ))}
          </div>
        )}
        <div className={styles.footer}>
          <button
            type="button"
            className={styles.skip}
            data-testid="dialogue-skip"
            onClick={() => {
              onDone(true);
            }}
          >
            {t('ui.dialogue.skip')}
          </button>
        </div>
      </div>
    </div>
  );
}
